import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useLoginMutation } from "@/api/authApi";
import { useAppSelector } from "@/store/hooks";
import { useEffect, useState } from "react";
import Button from "@mui/material/Button";

import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import InputAdornment from "@mui/material/InputAdornment";
import { Alert, Stack, TextField } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { type FormType, schema } from "@/schemas/login";
import { BrowserCompatibilityAlert } from "@/support/BrowserCompatibilityAlert.tsx";
import pkg from "../../package.json";
import { mutationFeedback } from "@/services/utils.tsx";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/login")({
  component: LoginPage,
});

const defaultValues = {
  email: import.meta.env.DEV ? "user@test.com" : "",
  password: import.meta.env.DEV ? "1234admin" : "",
};

function LoginPage() {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const [login, { isLoading, error }] = useLoginMutation();
  const { t } = useTranslation();

  const {
    control,
    setError,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues,
    mode: "onBlur",
    resolver: zodResolver(schema),
  });

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate({ to: "/home" });
    }
  }, [isAuthenticated, navigate]);

  const onSubmit = async (data: FormType) => {
    const { email, password } = data;

    const res = await login({
      email: email.trim(),
      password: password.trim(),
    });

    mutationFeedback(res, {
      success: t("auth.loginSuccess"),
    });
  };

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={(theme) => ({
        backgroundColor: theme.palette.background.default,
        height: "100vh",
      })}
    >
      <Box
        sx={(theme) => ({
          width: "100%",
          maxWidth: 500,
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          px: 4,
        })}
      >
        <Stack gap={2} sx={{ my: 6 }}>
          <BrowserCompatibilityAlert />
          <Typography variant="h3" sx={{ mb: 1.5 }}>
            {`Bienvenue sur Biogora !`}
          </Typography>
          {import.meta.env.DEV && (
            <Alert icon={false} severity="info">
              <strong>User</strong> : user@test.com // 1234admin
              <br />
              <strong>Admin</strong> : admin@test.com // 1234admin
            </Alert>
          )}
        </Stack>
        {}
        <form noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
          <Stack gap={2} sx={{ mb: 4 }}>
            <Controller
              name="email"
              control={control}
              rules={{ required: true }}
              render={({ field: { value, onChange, onBlur } }) => (
                <TextField
                  fullWidth
                  autoFocus
                  label={t("misc.email")}
                  value={value}
                  onBlur={onBlur}
                  onChange={onChange}
                  error={Boolean(errors.email)}
                  InputProps={{
                    size: "medium",
                  }}
                  inputProps={{
                    "data-testid": "login-email-input",
                  }}
                  {...(errors.email && {
                    helperText: errors.email.message,
                  })}
                />
              )}
            />
            <Box sx={{ mb: 1.5 }}>
              <Controller
                name="password"
                control={control}
                rules={{ required: true }}
                render={({ field: { value, onChange, onBlur } }) => (
                  <TextField
                    fullWidth
                    value={value}
                    onBlur={onBlur}
                    label={t("auth.password")}
                    onChange={onChange}
                    id="auth-login-v2-password"
                    error={Boolean(errors.password)}
                    {...(errors.password && {
                      helperText: errors.password.message,
                    })}
                    type={showPassword ? "text" : "password"}
                    InputProps={{
                      size: "medium",
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            edge="end"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => setShowPassword(!showPassword)}
                            data-testid={"show-password-button"}
                          >
                            {showPassword ? (
                              <VisibilityIcon />
                            ) : (
                              <VisibilityOffIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{
                      "data-testid": "login-password-input",
                    }}
                  />
                )}
              />
            </Box>

            <Button fullWidth type="submit" variant="contained" sx={{ mb: 4 }}>
              Login
            </Button>
            <Typography sx={{ alignSelf: "flex-end" }}>
              v.{pkg.version}
            </Typography>
          </Stack>
        </form>
      </Box>
    </Stack>
  );
}
